/**
 * The call list: a set of numbers worked down one after another.
 *
 * Cold calling is the main job of this CRM and until now every call was its own
 * event - dial, hang up, go back to the table, remember where you were. This is
 * the state that makes a two-hundred-row filter into one run: which numbers, in
 * what order, which one is up, and how many are left.
 *
 * Pure on purpose. Nothing here knows about React, the Voice SDK or the server;
 * `useCallQueue.ts` holds it in state and `EndOfCall.tsx` draws it. Everything
 * that decides WHICH number gets dialled next is therefore testable without a
 * browser, which matters more here than in most places: getting it wrong means
 * calling somebody twice, or calling somebody who asked us not to.
 */
import type { CrmLabel } from "../../../lib/crmLabels";
import { normalizeE164 } from "../../../lib/phone";
import type { CallTarget } from "../callState";

/** Why rows that were picked never made it into the run. Counted rather than
 *  listed: the operator needs to know the list is shorter than their selection
 *  and why, not which of two hundred rows it was. */
export interface QueueSkips {
  /** The number could not be read as a phone number at all. */
  invalid: number;
  /** The number is on the do-not-call list. */
  suppressed: number;
  /** The same number appeared on more than one selected row. */
  duplicate: number;
}

export interface CallQueue {
  /**
   * The run, in the order it will be dialled.
   *
   * Their `phone` is ALREADY normalized - unlike every other `CallTarget` in
   * this app, which carries whatever the register or the scanner scraped. The
   * queue needs one canonical string because it is also the identity it dedupes
   * and checks the do-not-call list by, and two spellings of one number would
   * be two calls to one company.
   */
  targets: CallTarget[];
  /** Which of them is up. Zero-based; `queueProgress` says it in human terms. */
  cursor: number;
  skipped: QueueSkips;
}

const NO_SKIPS: QueueSkips = { invalid: 0, suppressed: 0, duplicate: 0 };

/** Did anything at all get left out? Drives whether the note is drawn. */
export function anySkipped(skips: QueueSkips): boolean {
  return skips.invalid > 0 || skips.suppressed > 0 || skips.duplicate > 0;
}

/**
 * Turn a selection into a run.
 *
 * Three things are dropped here rather than discovered one dial at a time, and
 * each of them is a call the operator would otherwise have had to make and
 * abandon: a number that is not a number (the register is scraped free text),
 * a number somebody has asked us to stop calling, and the same company picked
 * twice under two rows. Doing it up front is also the only way the progress
 * count can be honest - "1/17" of a list that will really be dialled, not
 * "1/20" that quietly becomes seventeen.
 *
 * Returns null when nothing survived. The caller has to say so; starting a run
 * of zero calls and showing an empty window would read as a broken button.
 */
export function buildQueue(
  rows: readonly CallTarget[],
  isSuppressed: (e164: string) => boolean,
): CallQueue | null {
  const skipped: QueueSkips = { ...NO_SKIPS };
  const seen = new Set<string>();
  const targets: CallTarget[] = [];

  for (const row of rows) {
    const phone = normalizeE164(row.phone);
    if (!phone) {
      skipped.invalid += 1;
      continue;
    }
    if (isSuppressed(phone)) {
      skipped.suppressed += 1;
      continue;
    }
    if (seen.has(phone)) {
      skipped.duplicate += 1;
      continue;
    }
    seen.add(phone);
    // The normalized number replaces the scraped one: see `CallQueue.targets`.
    targets.push({ ...row, phone });
  }

  if (targets.length === 0) return null;
  return { targets, cursor: 0, skipped };
}

/** The number being called, or null on a queue whose cursor has run off the end
 *  (which `advanceQueue` never produces - it returns null instead). */
export function queueAt(queue: CallQueue): CallTarget | null {
  return queue.targets[queue.cursor] ?? null;
}

/**
 * Move to the next number, and return null when there is no next number.
 *
 * The do-not-call list is consulted AGAIN here, not only at build time, and
 * that is the whole reason this is not a `cursor + 1`. An operator who is told
 * "stop calling us" marks the number mid-run, and the same company can sit
 * further down the same selection under a second row with a second number. The
 * marked ones are removed from the tail rather than skipped over, so the
 * progress count stays a count of calls that will really be made.
 */
export function advanceQueue(
  queue: CallQueue,
  isSuppressed: (e164: string) => boolean,
): CallQueue | null {
  const done = queue.targets.slice(0, queue.cursor + 1);
  const rest = queue.targets.slice(queue.cursor + 1).filter((t) => !isSuppressed(t.phone));
  if (rest.length === 0) return null;
  return { ...queue, targets: [...done, ...rest], cursor: done.length };
}

/** Where the run is, as the two numbers the operator reads: "4 / 17". */
export function queueProgress(queue: CallQueue): { position: number; total: number } {
  return { position: queue.cursor + 1, total: queue.targets.length };
}

/** Whether the number now up is the last one. The button says "finish" rather
 *  than "next" on it, so pressing it is not a surprise. */
export function queueLast(queue: CallQueue): boolean {
  return queue.cursor >= queue.targets.length - 1;
}

/** Why "next" is refused, or "" when it is not. One string rather than a
 *  boolean: the screen has to say which rule is holding the run up. */
export type NextBlock = "" | "no_outcome";

/**
 * The wrap-up rule: a call gets an outcome before the next one starts.
 *
 * This is what turns wrap-up from a good intention into something that
 * happens. Without it the operator presses "next" while the last call is still
 * in their head, and by the evening the run has produced twenty rows that say
 * nothing about how any of it went.
 *
 * It fails OPEN in the two cases where demanding a choice would be a dead end,
 * and both are real rather than theoretical:
 *
 * - **No labels in the registry.** `crm.labels.calls` is empty until somebody
 *   fills it in on the settings page. A gate over an empty menu is a run that
 *   cannot be continued and a button whose reason cannot be satisfied.
 * - **No CallLog row.** A number Twilio refused, a 409 from the do-not-call
 *   list, telephony not configured - the attempt never became a row, so there
 *   is nothing to hang a label on.
 *
 * The tags are checked AGAINST the registry, not merely counted. A row can be
 * carrying the id of a label that was deleted weeks ago; that id draws no chip
 * (see `labelsOf`), so counting it would satisfy a gate whose screen shows
 * nothing selected.
 */
export function nextBlocked(input: {
  labels: readonly CrmLabel[];
  tags: readonly string[];
  callLogId: string | null;
}): NextBlock {
  if (input.labels.length === 0) return "";
  if (!input.callLogId) return "";
  const known = new Set(input.labels.map((l) => l.id));
  return input.tags.some((tag) => known.has(tag)) ? "" : "no_outcome";
}
